import { useEffect, useState } from "react";

import type { EventBuilders } from "@spt-tracking/pulse-sdk";

type TCFCategories =
  | "CMP:marketing"
  | "CMP:analytics"
  | "CMP:personalisation"
  | "CMP:advertising";

type TCFPermission = "1" | "0";

interface TCF {
  getPermission: (
    category: TCFCategories,
    callback: (value: TCFPermission) => void,
  ) => void;
  subscribe: (
    category: TCFCategories,
    callback: (value: TCFPermission) => void,
  ) => () => Promise<void>;
  showPrivacyManager: () => void;
  getConsentedToAllSync: () => boolean;
  isConsentedToAll(callback: (value: boolean) => void): () => void;
  getCachedOrDefaultConsentsForPulse: () => EventBuilders.Consents;
  onTcfReady: (cb: () => void) => void;
}

function getTCF(): Promise<TCF> {
  // Check if window is available before creating any timers
  if (typeof window === "undefined") {
    return Promise.reject(new Error("window is not defined"));
  }

  return new Promise((resolve) => {
    setTimeout(() => {
      // Double-check window still exists (for test teardown scenarios)
      if (typeof window === "undefined") {
        return;
      }
      const cookieConsent = window._tcf_;
      if (cookieConsent) {
        resolve(cookieConsent);
      } else {
        resolve(getTCF());
      }
    }, 100);
  });
}

// Consent cache with metadata for hybrid invalidation strategy
interface ConsentCache {
  value: boolean | null;
  pathname: string;
  timestamp: number;
}

let consentCache: ConsentCache | null = null;
let consentPromise: Promise<boolean | null> | null = null;

// Cache TTL: 5 minutes (consent rarely changes within a session)
const CONSENT_CACHE_TTL = 5 * 60 * 1000;

/**
 * Get consent with hybrid caching strategy:
 * - Caches result per pathname (invalidates on navigation)
 * - Expires after TTL (prevents stale data)
 * - Supports manual invalidation for CMP changes
 */
async function isConsentToAll(): Promise<boolean | null> {
  const currentPathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const now = Date.now();

  // Invalidate cache if:
  // 1. Different pathname (user navigated)
  // 2. Cache expired (TTL exceeded)
  // 3. No cache exists
  if (
    !consentCache ||
    consentCache.pathname !== currentPathname ||
    now - consentCache.timestamp > CONSENT_CACHE_TTL
  ) {
    consentCache = null;
    consentPromise = null;
  }

  if (consentCache) {
    return consentCache.value;
  }

  if (consentPromise) {
    return consentPromise;
  }

  consentPromise = getTCF()
    .then((tcf) => {
      const consent = tcf.getConsentedToAllSync();

      consentCache = {
        value: consent,
        pathname: currentPathname,
        timestamp: now,
      };
      consentPromise = null;

      return consent;
    })
    .catch((error) => {
      console.error("isConsentToAll: Failed to get consent from TCF", error);
      consentPromise = null;
      return null;
    });

  return consentPromise;
}

/**
 * Manually invalidate consent cache
 * Call this if user changes consent preferences via CMP
 */
const invalidateConsentCache = () => {
  consentCache = null;
  consentPromise = null;
};

function getTCFSync(cb: (sdk: TCF) => void): void {
  getTCF().then(cb);
}

function transformPermissionValue(value: string): boolean {
  return Boolean(Number(value));
}

const triggerPrivacyManager = async () => {
  const cookieConsent = await getTCF();
  return cookieConsent.showPrivacyManager();
};

function useTcfPermission(category: TCFCategories) {
  const [permission, setPermission] = useState<null | boolean>(null);

  useEffect(() => {
    getTCFSync((tcf) => {
      tcf.subscribe(category, (value: number | string) => {
        setPermission(Boolean(Number(value)));
      });
    });
  }, [category]);

  return permission;
}

const triggerTCFSettingsModal = async () => {
  const tcf = await getTCF();
  tcf.showPrivacyManager();
};

const isConsentSet = (consents: EventBuilders.Consents) =>
  Object.values(consents.purposes).every((entry) => entry.status !== "unknown");

function waitForConsent(tcfClient: TCF): Promise<EventBuilders.Consents> {
  return new Promise((resolve) => {
    const consents = tcfClient.getCachedOrDefaultConsentsForPulse();
    if (!isConsentSet(consents)) {
      setTimeout(() => {
        resolve(waitForConsent(tcfClient));
      }, 2000);
    } else {
      resolve(consents);
    }
  });
}

export type { TCFPermission, TCFCategories, TCF };
export {
  useTcfPermission,
  triggerPrivacyManager,
  transformPermissionValue,
  getTCFSync,
  isConsentToAll,
  invalidateConsentCache,
  getTCF,
  triggerTCFSettingsModal,
  waitForConsent,
};
